// noinspection ES6UnusedImports
import React, {useEffect, useState} from 'react';
import CartTop from './CartTop.jsx';
import CartItemsList from './CartItemsList.jsx';
import CartSummary from './CartSummary.jsx';
import BenefitsSection from './BenefitsSection.jsx';
import LastCheckCartTop from './LastCheckCartTop.jsx';
import CartEmpty from './CartEmpty.jsx';
import addCoupon from './coupon/add-coupon';
import deleteCoupon from './coupon/delete-coupon';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import Messages from '@core/scripts/components/messages';
import Url from '@core/scripts/helper/url';
import ChangeItemLayer from '@core/scripts/components/change-item/ChangeItemLayer.jsx';
import changeItem from '@core/scripts/components/actions/change-item';
import Landmark from '@core/scripts/components/landmark-adapter';
import ChangeItemLandmarks from '@core/scripts/components/change-item/changeItemLandmarks';
import fetchWithCSRF from '@core/scripts/helper/fetch-with-csrf';
import openLayer from '@core/scripts/components/layers/open-layer';
import CheckoutErrorLayer from '@core/scripts/components/layers/CheckoutErrorLayer.jsx';
import Logger from '@core/scripts/components/logger';
import ModelContainer from '@core/scripts/components/model-container';
import NitroEventHelper from '@core/scripts/helper/nitro-event-helper';
import TrackingEventFactory from '../tracking/checkout-tracking-event-factory';
import {Grid, Button} from '@bonprix/design-system';
import {addAppResumeEventListener} from '@checkout/components/last-check/HandleAppResume.jsx';
import EmptyWishlistValueProvider from '@wishlist/components/context/EmptyWishlistValueProvider';

export const ShowCouponInfoAccordionContext = React.createContext({});

export default function Cart({
                                 hideHeaderButton,
                                 footerText,
                                 footerSubmit,
                                 hideBenefits,
                                 hideBackButton,
                                 isLastCheckPage,
                                 lastCheckTexts,
                                 hideFooterDivider,
                                 model,
                                 formSubmitted,
                                 hideLoginHint,
                                 isPhoneNumberRequiredButMissing,
                                 page,
                                 showErrorLayer,
                                 itemHeaderComponentSize,
                                 tosUrl,
                                 recoParams
                               }) {
    const texts = model.texts;
    const selectedDeliveryType = model.selectedDeliveryType;
    const country = model.country;
    const guest = model.guest;

    const [currentCart, setCurrentCart] = useState(model.cart);
    const [couponPending, setCouponPending] = useState(false);
    const [couponError, setCouponError] = useState('');
    const [calculatingPriceItems, setCalculatingPriceItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCouponInfoAccordion, setShowCouponInfoAccordion] = useState(currentCart?.showCouponInfoAccordion);

    useEffect(() => {
        const subscriptions = [
            MessageBroker.subscribe(Messages.CART.ITEM_CHANGE_SUCCESS, ({origin, isLastCheckPage, data}) => {
                if (['cart', 'cartLayer'].indexOf(origin) === -1) {
                    return;
                }

                const {cart} = data?.lastCheckSummary || data;

                if (cart?.coupon?.couponMessages) {
                    setCouponError(cart.coupon.couponMessages[0].description);
                    setShowCouponInfoAccordion(cart.showCouponInfoAccordion);
                }

                // NH-35: Empty cart handling only for last-check page
                if (isLastCheckPage) {
                    if (!cart) {
                        window.location = Url.toAbsolute('/');
                    }
                    return setCurrentCart(cart);
                }

                return setCurrentCart(cart);
            }),

            MessageBroker.subscribe(Messages.CART.ITEM_ADD_SUCCESS, () => {
                fetchWithCSRF(Url.toAbsolute('/ajax/list/cart/'), {
                    method: 'GET',
                    headers: new Headers({
                        'Content-Type': 'application/json',
                    }),
                })
                    .then((response) => {
                        if (response.ok) {
                            return response.json();
                        }
                        return null;
                    })
                    .then((cart) => {
                        return setCurrentCart(cart);
                    });
            }),
        ];

        // componentWillUnmount
        return () => {
            subscriptions.forEach((subscription) => MessageBroker.unsubscribe(subscription));
        };
    }, []);

    useEffect(() => {
        if (currentCart === null || !isObjectEmpty(showErrorLayer)) {
            openLayer(CheckoutErrorLayer, {texts});
        }
    }, [currentCart, showErrorLayer]);

    useEffect(() => {
        if (currentCart && currentCart.coupon && currentCart.coupon.couponMessages) {
            setCouponError(currentCart.coupon.couponMessages[0].description);
        }
    }, [currentCart]);

    const isObjectEmpty = (objectName) => {
        return (
            objectName &&
            Object.keys(objectName).length === 0 &&
            objectName.constructor === Object
        );
    };

    if (currentCart == null || currentCart.items === null || currentCart.items.length === 0) {
        return <EmptyWishlistValueProvider userIsLoggedIn={false} isWishlistEmpty={false} wishlistCount={1}>
                    <CartEmpty texts={texts} hideLoginHint={hideLoginHint} recoParams={recoParams} />
                </EmptyWishlistValueProvider>
    }

    const onChangeItemQuantity = (item, newQuantity, changeType, items) => {
        Landmark.sendClickLandmark('basket_change_quantity_direct');
        return onUpdateItem(item, newQuantity, changeType, items);
    }

    const onDeleteItem = (item, newQuantity, items) => {
        return onUpdateItem(item, newQuantity, 'DELETE', items);
    }

    const onUpdateItem = async (item, newQuantity, changeType, items) => {
        const {id, itemId, itemOptionId, quantity: oldQuantity, itemNumber, productId, singlePriceValue} = item;
        const posNr = items?.indexOf(item) + 1;
        if (!calculatingPriceItems.includes(id)) {
            setCalculatingPriceItems((prevState) => {
                return [...prevState, id];
            });
        }

        try {
            await changeItem(
                id,
                itemId,
                itemOptionId,
                newQuantity,
                'cart',
                isLastCheckPage,
                oldQuantity,
                itemNumber,
                productId,
                singlePriceValue,
                posNr
            );

            ChangeItemLandmarks.sendAddActionLandmark(item, oldQuantity, newQuantity, changeType);
            await sendNitroEvent(item, oldQuantity, newQuantity);
            setCalculatingPriceItems((prevState) => {
                return prevState.filter((calculatingPriceItem) => calculatingPriceItem !== id);
            });
        } catch (exception) {
            openLayer(CheckoutErrorLayer, {texts});
            Logger.error('Error while changing quantity of item', exception);
        }
    };

    const onAddCoupon = (coupon) => {
        setCouponPending(true);
        addCoupon(coupon, onAddCouponSuccess, onAddCouponFailure, onAddCouponRequestFailure, page);
    };

    const onDeleteCoupon = () => deleteCoupon(onRemoveCoupon, () => openLayer(CheckoutErrorLayer, {texts}));

    const onAddCouponSuccess = (cart) => {
        setCurrentCart(cart);
        setCouponPending(false);
        setCouponError('');
    };

    const onAddCouponFailure = (errorText, showCouponInfoAccordion) => {
        setShowCouponInfoAccordion(showCouponInfoAccordion);
        setCouponPending(false);
        if (errorText) {
            setCouponError(errorText);
        } else {
            setCouponError(texts.cot_coupon_invalid_or_unknown);
        }
    };

    const onAddCouponRequestFailure = () => {
        setCouponPending(false);
        openLayer(CheckoutErrorLayer, {texts});
    };

    const onRemoveCoupon = (cart) => {

        if (cart?.coupon?.couponMessages) {
            setCouponError(cart.coupon.couponMessages[0].description);
        } else if (couponError !== '') {
            setCouponError('');
        }

        setCurrentCart(cart);
        setCouponPending(false);
    };

    const onCouponInput = () => {
        setCouponError('');
    };

    const onCartButtonClick = () => {
        if (isLastCheckPage) {
            setLoading(true);
            addAppResumeEventListener();
            Landmark.sendClickLandmark('bestellen');
        } else {
            setLoading(true);
            Landmark.sendClickLandmark('basket_checkout');
        }
        footerSubmit || !loading && open(Url.toAbsolute('/buy/'), '_self');
        document.getElementById('lastcheck-form')?.submit();
    };

    const selectedDeliveryTypeIsPickUpLocation = (selectedDeliveryType) => {
        return selectedDeliveryType === 'HERMES' || selectedDeliveryType === 'pickUp';
    };

    const containsAtLeastOneItemWithPickUpLocationRestriction = (items) => {
        return !!items.find((item) => item.deliverableToPickUpLocation === false);
    };

    const isProblematicPickUpLocationOrder = (selectedDeliveryType, items) => {
        return (
            selectedDeliveryTypeIsPickUpLocation(selectedDeliveryType) &&
            containsAtLeastOneItemWithPickUpLocationRestriction(items)
        );
    };

    const containsDeliverableItems = (items) => {
        return items.some((item) => item.deliverable);
    };

    const canOrderNotBeSubmitted = (items) => {
        return (
            isProblematicPickUpLocationOrder(selectedDeliveryType, items) ||
            !containsDeliverableItems(items) ||
            isPhoneNumberRequiredButMissing
        );
    };

    // If all items in cart are not deliverable -> disable CartToCheckout-Button
    const disableCartToCheckoutButton = !containsDeliverableItems(currentCart.items);
    // If all items in cart are not deliverable OR PickUpLocation Delivery not possible OR cart contains VAL item but no phone number was provided -> disable Order-Button
    const disableOrderButton = canOrderNotBeSubmitted(currentCart.items);

    const onClickBackButton = () => {
        window.open(Url.toAbsolute('/'), '_self');
    };

    let backButton;
    if (!hideBackButton) {
        backButton = (
            <div className="cart__button-pre-container flex-none l:px-2.5">
                <Button
                    icon="arrow-left"
                    variant={'secondary'}
                    className="cart__button-prev"
                    left={true}
                    onClick={onClickBackButton}
                >
                    {texts.cot09_wk_continue_shopping_with_bonprix}
                </Button>
            </div>
        );
    }

    let top;
    if (isLastCheckPage) {
        top = (
            <LastCheckCartTop
                cart={currentCart}
                texts={lastCheckTexts}
                isDeliveryToPickUpLocationNotPossible={isProblematicPickUpLocationOrder(
                    selectedDeliveryType,
                    currentCart.items
                )}
            />
        );
    } else {
        top = (
            <CartTop
                cart={currentCart}
                texts={texts}
                hideHeaderButton={hideHeaderButton}
                onCartButtonClick={onCartButtonClick}
                disableCartToCheckoutButton={disableCartToCheckoutButton}
                loading={loading}
                isDeliveryToPickUpLocationNotPossible={isProblematicPickUpLocationOrder(
                    selectedDeliveryType,
                    currentCart.items
                )}
            />
        );
    }

    async function sendNitroEvent(orderCartItem, oldQuantity, newQuantity) {
        if (oldQuantity > newQuantity) {
            await NitroEventHelper.trigger(
                NitroEventHelper.EventType.REMOVE_FROM_CART,
                TrackingEventFactory.createChangeCartFromEditLayerEvent(
                    orderCartItem,
                    oldQuantity - newQuantity,
                    ModelContainer.get('pageViewId')
                )
            );
        } else if (oldQuantity < newQuantity) {
            await NitroEventHelper.trigger(
                NitroEventHelper.EventType.ADD_TO_CART,
                TrackingEventFactory.createChangeCartFromEditLayerEvent(
                    orderCartItem,
                    newQuantity - oldQuantity,
                    ModelContainer.get('pageViewId')
                )
            );
        }
    }

    return (
        <Grid>
            <Grid.Item s={1} />
            <Grid.Item s={10}>
                <div className="flex flex-col w-full">
                    {top}
                    <CartItemsList
                        items={currentCart.items}
                        texts={texts}
                        calculatingPriceItems={calculatingPriceItems}
                        onChangeItemQuantity={onChangeItemQuantity}
                        onDeleteItem={onDeleteItem}
                        isPickUpLocationSelectedDeliveryType={selectedDeliveryTypeIsPickUpLocation(selectedDeliveryType)}
                        page={page}
                        itemHeaderComponentSize={itemHeaderComponentSize}
                    />
                    <ShowCouponInfoAccordionContext.Provider value={{showCouponInfoAccordion, setShowCouponInfoAccordion}}>
                        <CartSummary
                            cart={currentCart}
                            texts={texts}
                            footerSubmit={footerSubmit}
                            footerText={footerText}
                            calculating={calculatingPriceItems.length > 0}
                            onAddCoupon={onAddCoupon}
                            onDeleteCoupon={onDeleteCoupon}
                            couponPending={couponPending}
                            couponError={couponError}
                            onCouponInput={onCouponInput}
                            onCartButtonClick={onCartButtonClick}
                            disableOrderButton={disableOrderButton}
                            disableCartToCheckoutButton={disableCartToCheckoutButton}
                            isLastCheckPage={isLastCheckPage}
                            loading={loading || formSubmitted}
                            isParcelShopDeliveryPossible={!containsAtLeastOneItemWithPickUpLocationRestriction(currentCart.items)}
                            page={page}
                            tosUrl={tosUrl}
                        />
                    </ShowCouponInfoAccordionContext.Provider>
                    {hideBenefits ? null : <BenefitsSection
                        texts={texts}
                        country={country}
                        paymentTypes={currentCart.paymentTypes}
                        guest={guest}
                    />}
                    {hideFooterDivider ? null : <div className="cart__divider--viewport-width mt-6" />}
                    {backButton}

                    <ChangeItemLayer
                        layerOrigin="cartLayer" // TODO: Or lastcheck
                        channelId="1" // TODO: Where to get this from?
                        pageType="cartLayer" // TODO: Or lastcheck
                        fkIdOverride={-95} // TODO: What is this?
                        isLastCheckPage={isLastCheckPage}
                    />
                </div>
            </Grid.Item>
        </Grid>
    );
}
